import { Injectable, inject } from '@angular/core';
import { map, Observable, tap } from 'rxjs';
import { SupportAccessResponse, SupportManagementService } from './support-management.service';
import { collectSupportPermissionCodes } from '../utils/support-page-access.util';

@Injectable({
  providedIn: 'root'
})
export class SupportAccessService {
  private readonly supportManagementService = inject(SupportManagementService);
  private readonly storageKey = 'support_permission_codes';
  private readonly legacyStorageKey = 'support_page_keys';

  getCachedPermissionCodes(): string[] {
    if (typeof window === 'undefined') {
      return [];
    }

    try {
      const raw = localStorage.getItem(this.storageKey);
      if (!raw) {
        return [];
      }

      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.filter((value): value is string => typeof value === 'string') : [];
    } catch (error) {
      console.warn('Failed to load cached support page keys', error);
      return [];
    }
  }

  cachePermissionCodes(permissionCodes: string[]): void {
    if (typeof window === 'undefined') {
      return;
    }

    localStorage.setItem(this.storageKey, JSON.stringify(collectSupportPermissionCodes(permissionCodes)));
  }

  clearCachedPermissionCodes(): void {
    if (typeof window === 'undefined') {
      return;
    }

    localStorage.removeItem(this.storageKey);
    localStorage.removeItem(this.legacyStorageKey);
  }

  getSupportAccess(): Observable<SupportAccessResponse> {
    return this.supportManagementService.getSupportAccess().pipe(
      map(response => {
        const payload = response.response;

        return {
          ...payload,
          permissionCodes: collectSupportPermissionCodes(payload?.permissionCodes ?? []),
          superAdminPermissionCodes: collectSupportPermissionCodes(payload?.superAdminPermissionCodes ?? [])
        };
      }),
      tap(response => this.cachePermissionCodes(response?.permissionCodes ?? []))
    );
  }
}
